import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { Component, inject, OnInit, output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { CardComponent } from '@vectronic/ui';
import { TimeService } from '@vectronic/util';
import { DatatableColumn, DisplayColumn } from '../../types/datatable-column';
@Component({
  selector: 'table-edit-columns',
  templateUrl: './edit-columns.component.html',
  styleUrls: ['./edit-columns.component.scss'],
  imports: [CardComponent, CdkDropListGroup, CdkScrollable, CdkDropList, CdkDrag, MatIcon],
})
export class EditColumnsComponent<T extends object> implements OnInit {
  private timeService = inject(TimeService);
  dialogRef = inject(MatDialogRef<EditColumnsComponent<T>>);
  data: {
    shownColumns: DisplayColumn<T>[];
    datatableColumns: DatatableColumn<T>[];
  } = inject(MAT_DIALOG_DATA);

  columnSelectionChanged = output<DisplayColumn<T>[]>();
  datatableColumns: DatatableColumn<T>[];

  visibleColumns: (DatatableColumn<T> | undefined)[];
  hiddenColumns: DatatableColumn<T>[];

  headerIconButtons = [
    {
      icon: 'arrow_back',
      onClick: () => this.showAll(),
      hoverText: 'Show all',
    },
    {
      icon: 'settings_backup_restore',
      onClick: () => this.restoreDefault(),
      hoverText: 'Restore default',
    },
    {
      icon: 'arrow_forward',
      onClick: () => this.hideAll(),
      hoverText: 'Hide all',
    },
  ];

  constructor() {
    this.data.shownColumns = this.data.shownColumns?.filter(
      (fieldName) => fieldName !== '#' && fieldName !== 'rowActions',
    );
    this.datatableColumns = this.data.datatableColumns?.filter(
      ({ fieldName }) => fieldName !== '#',
    );
  }

  ngOnInit() {
    const { shownColumns } = this.data;
    if (this.timeService.shouldShowLocalTime()) {
      this.visibleColumns = shownColumns?.map((displayColumn) =>
        this.datatableColumns?.find(({ fieldName }) => fieldName === displayColumn),
      );
      this.hiddenColumns = this.datatableColumns?.filter(
        ({ fieldName }) => !shownColumns?.includes(fieldName),
      );
    } else {
      this.visibleColumns = shownColumns
        ?.map((displayColumn) =>
          this.datatableColumns?.find(({ fieldName }) => fieldName === displayColumn),
        )
        ?.filter((col) => !col?.fieldName?.includes('.lmt'));
      this.hiddenColumns = this.datatableColumns
        ?.filter(({ fieldName }) => !shownColumns?.includes(fieldName))
        ?.filter((col) => !col?.fieldName?.includes('.lmt'));
    }
  }

  private hideAll() {
    const firstColumn = this.visibleColumns.shift();
    this.hiddenColumns = [...this.hiddenColumns, ...this.visibleColumns];
    this.visibleColumns = [firstColumn];
    this.columnSelectionChanged.emit(this.visibleColumns.map((item) => item?.fieldName));
  }

  private showAll() {
    this.visibleColumns = [...this.visibleColumns, ...this.hiddenColumns];
    this.hiddenColumns = [];
    this.columnSelectionChanged.emit(this.visibleColumns.map((item) => item?.fieldName));
  }

  private restoreDefault() {
    const allShownColumns = [...this.visibleColumns, ...this.hiddenColumns];
    this.visibleColumns = [
      ...this.datatableColumns.filter((column) => allShownColumns.includes(column) && !column.hide),
    ];
    this.hiddenColumns = [
      ...this.datatableColumns.filter((column) => allShownColumns.includes(column) && column.hide),
    ];
    this.columnSelectionChanged.emit(this.visibleColumns.map((item) => item?.fieldName));
  }

  drop(event: CdkDragDrop<DatatableColumn<T>[]>) {
    const { previousContainer, container, previousIndex, currentIndex } = event;
    if (previousContainer === container) {
      moveItemInArray(container.data, previousIndex, currentIndex);
    } else {
      transferArrayItem(previousContainer.data, container.data, previousIndex, currentIndex);
    }
    this.columnSelectionChanged.emit(this.visibleColumns.map((item) => item?.fieldName));
  }
}
